// OrthodonticData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const orthodontic: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Orthodontic Dubai",
            "description": "Orthodontic Treatment in Dubai - Straighten Your Smile, Transform Your Life with Elite Body Home Polyclinic",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Orthodontic Dubai"],
        "hero": {
            "title": "Orthodontic Treatment in Dubai",
            "subtitle": "Straighten Your Smile, Transform Your Life!",
            "description": "Orthodontics is a dental specialty that deals with the diagnosis, prevention, and correction of teeth and jaws that are not in proper alignment. It is essential for the improvement of your smile as well as the maintenance of your overall oral health. Leading Orthodontic Treatments in Turkey | Elite Body Home Polyclinic"
        },
        "howItWorks": {
            "title": "Process Of Orthodontic treatment procedure",
            "description": "A teeth straightener is a dental device used to align your teeth through orthodontic treatment which realigns your teeth using braces or aligners over a period. It is a well-planned procedure that provides long-lasting effect making your smile healthy."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "An orthodontist will evaluate your teeth, take X-rays and consult with you on your specific concerns and goals.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Customized Treatment Plan",
                    "desc": "A treatment plan is customized according to your needs and may include braces, clear aligners, or other devices.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "Fitting and Adjustments",
                    "desc": "They, then, fit braces or aligners, with regular adjustments to ensure progress.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Monitoring and Maintenance",
                    "desc": "The orthodontist periodically adjusts the aligners to ensure that teeth are tracking properly and moving into their intended positions.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Orthodontic treatment benefits go far beyond just a pretty smile. Advantages functional and health:",
            "items": [
                {
                    "title": "Improved Oral Health",
                    "desc": "Correctly aligned teeth are simpler to clean, which lowers the chance of cavities, gum disease and more.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Better Functionality",
                    "desc": "When your teeth are properly aligned, it can improve your bite and lessen the risk of jaw pain, TMJ disorders, and chewing problems.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Enhanced Self-Esteem",
                    "desc": "A dazzling smile can improve one with regards to confidence and all prosperity.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Long-Term Stability",
                    "desc": "With correctly positioned teeth, the likelihood of orthodontic work is also lessened in future.",
                    "icon": "fa-solid fa-shield-alt"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Orthodontics can be done at a young age or later in life, and the correction of problems at a younger age is often more beneficial. But many adults receive orthodontic treatment as well, even later in life.",
            "items": [
                {
                    "title": "Children",
                    "desc": "Intervening early, typically at the age of 7, could avoid more serious problems later on; by then, teeth and the jaw are still growing.",
                    "icon": "fa-solid fa-child"
                },
                {
                    "title": "Dental Health",
                    "desc": "Candidates also need to have healthy gums and teeth because conditions like gum disease or cavities must be treated prior to beginning treatment.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Jaw Alignment Issues",
                    "desc": "People who have bite problems, such as overbite, underbite, crossbite, or open bite, are good candidates to receive orthodontic treatment.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Crowding or Spacing Problems",
                    "desc": "Orthodontics can be beneficial for patients with crowded, crooked teeth or for those with excessive space between their teeth.",
                    "icon": "fa-solid fa-arrows-left-right"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "There are multiple ways you can prepare your teeth for upcoming orthodontic treatment. If everything is well-prepared, treatment can be streamlined and better results can be achieved in a shorter period of time.",
            "items": [
                {
                    "title": "Consultation and Diagnosis",
                    "desc": "At your first appointment, the orthodontist will discuss your dental history, take X-rays and develop a treatment plan.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Oral Hygiene Routine",
                    "desc": "Orthodontic appliances may hinder the cleaning process, so develop a strict oral care routine from the beginning.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Dietary Adjustments",
                    "desc": "Do not eat anything sticky or hard that can damage the braces or disrupt the treatment.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Regular Dental Checkups",
                    "desc": "Get your teeth and gums healthy before applying for orthodontic treatment.",
                    "icon": "fa-solid fa-calendar-alt"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Orthodontic treatment's outcomes change lives in appearance, function, and health. Your expectations after treatment",
            "items": [
                {
                    "title": "Straighter Teeth",
                    "desc": "Healthy teeth contribute to a healthier smile that creates a beautiful face.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Improved Oral Health",
                    "desc": "They allow for proper cleaning of the teeth, helping to minimize the risk of decay, gum disease, and other dental problems.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Long-Term Stability",
                    "desc": "A retainer will help keep your teeth in their corrected position for many years to follow.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Increased Confidence",
                    "desc": "Many patients report a huge lift in self-esteem and social confidence after treatment.",
                    "icon": "fa-solid fa-face-smile-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "",
                    "desc": "",
                    "icon": "fa-solid fa-water"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How do I know if I need a night guard?",
                    "answer": "The duration of treatment varies depending on the complexity of your case, but it typically ranges from 12 to 24 months."
                },
                {
                    "question": "Is orthodontic treatment painful?",
                    "answer": "You may experience mild discomfort, especially after adjustments, but it is generally manageable with over-the-counter pain relievers."
                },
                {
                    "question": "Are there alternatives to traditional braces?",
                    "answer": "Yes, modern options like clear aligners and lingual braces (braces placed behind the teeth) are available for those seeking a more discreet treatment option."
                },
                {
                    "question": "Do I need a retainer after treatment?",
                    "answer": "Yes, wearing a retainer after treatment is essential to prevent teeth from shifting back to their original positions."
                },
                {
                    "question": "How often should I visit the orthodontist during treatment?",
                    "answer": "Typically, visits are scheduled every 4–6 weeks to monitor progress and make necessary adjustments."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    ar: {
        "url": "",
        "meta": {
          "title": "تقويم الأسنان في دبي",
          "description": "تقويم الأسنان في دبي - صحح ابتسامتك، وغيّر حياتك مع عيادات ايليت بودي هوم",
          "robots": "",
          "canonical": "",
          "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تقويم الأسنان"],
        "hero": {
          "title": "تقويم الأسنان في دبي",
          "subtitle": "صحح ابتسامتك، وغيّر حياتك",
          "description": "تقويم الأسنان هو فرع تخصصي في طب الأسنان يُعنى بتشخيص وتصحيح مشكلات اتساق الأسنان والفكين ومنع حدوثها ومعالجة العيوب السنية والوجهية. لا يقتصر دوره على تحسين مظهر الابتسامة فقط، بل يلعب أيضًا دورًا مهمًا في الحفاظ على صحة الفم بشكل عام. أفضل علاج تقويم الأسنان في دبي | عيادات ايليت بودي هوم"
        },
        "howItWorks": {
          "title": "خطوات علاج تقويم الأسنان",
          "description": "يُستخدم جهاز تقويم الأسنان لتصحيح مواضع الأسنان وتحسين اصطفافها وتصحيح مشاكل الفكين من خلال علاج تقويمي يعتمد على التقويم المعدني أو التقويم الشفاف على مدار فترة زمنية محددة. وهو إجراء مُخطط بعناية يوفر نتائج طويلة الأمد، مما يساعدك على الحصول على ابتسامة صحية."
        },
        "procedureSteps": {
          "heading": "",
          "para": "",
          "steps": [
            {
              "title": "الاستشارة الأولية",
              "desc": "يقوم طبيب التقويم بتقييم حالة أسنانك والتقاط صور الأشعة السينية، ومناقشة مخاوفك وأهدافك العلاجية بالتفصيل.",
              "icon": "fa-solid fa-calendar-check"
            },
            {
              "title": "خطة علاجية مخصصة",
              "desc": "تُصمم خطة علاجية وفقًا لاحتياجاتك، وقد تشمل التقويم المعدني، أو التقويم الشفاف، أو أجهزة تقويم أخرى.",
              "icon": "fa-solid fa-clipboard-list"
            },
            {
              "title": "التركيب والتعديلات",
              "desc": "يتم تركيب التقويم المعدني أو الشفاف، مع إجراء تعديلات منتظمة لضمان التقدم الصحيح في العلاج.",
              "icon": "fa-solid fa-teeth"
            },
            {
              "title": "المتابعة والعناية",
              "desc": "يقوم طبيب التقويم بإجراء تعديلات دورية على التقويم للتأكد من أن الأسنان تتحرك بالشكل الصحيح نحو مواقعها المستهدفة.",
              "icon": "fa-solid fa-chart-line"
            }
          ]
        },
        "benefits": {
          "heading": "الفوائد",
          "subheading": "",
          "para": "تتجاوز فوائد علاج تقويم الأسنان مجرد الحصول على ابتسامة جميلة، إذ تشمل أيضًا مزايا وظيفية وصحية مهمة، إذ تشمل أيضًا مزايا وظيفية وصحية مهمة:",
          "items": [
            {
              "title": "تحسين صحة الفم",
              "desc": "يسهل تنظيف الأسنان المصطفة بشكل صحيح، مما يقلل من خطر تسوس الأسنان، وأمراض اللثة، وغيرها.",
              "icon": "fa-solid fa-tooth"
            },
            {
              "title": "تحسين الأداء الوظيفي",
              "desc": "يساهم اصطفاف الأسنان الصحيح في تحسين العضات أو الإطباق، ويقلل من احتمالية الشعور بألم في الفك، وتخفيف اضطرابات المفصل الفكي الصدغي وصعوبة المضغ.",
              "icon": "fa-solid fa-utensils"
            },
            {
              "title": "تعزيز الثقة بالنفس",
              "desc": "تُعزز الابتسامة الجذابة والصحية من شعور الشخص بالثقة والراحة النفسية.",
              "icon": "fa-solid fa-face-smile"
            },
            {
              "title": "استقرار طويل الأمد",
              "desc": "عند تصحيح مواضع الأسنان، تقل احتمالية الحاجة إلى علاجات تقويمية إضافية في المستقبل.",
              "icon": "fa-solid fa-shield-alt"
            }
          ]
        },
        "eligibility": {
          "heading": "الأهلية",
          "subheading": "",
          "para": "يمكن إجراء تقويم الأسنان في سن مبكر أو حتى في مراحل لاحقة من العمر، إلا أن التدخل المبكر غالبًا ما يكون أكثر فاعلية من الجهة العلاجية. ومع ذلك، فإن العديد من البالغين يخضعون لتقويم الأسنان بنجاح حتى في مراحل متقدمة من حياتهم.",
          "items": [
            {
              "title": "الأطفال",
              "desc": "يفضل بدء تقييم حالة الطفل في سن السابعة تقريبًا، إذ لا يزال الأسنان والفك في طور النمو، ما يمنح فرصة للتصحيح قبل أن تتفاقم.",
              "icon": "fa-solid fa-child"
            },
            {
              "title": "صحة الفم",
              "desc": "يشترط أن يكون المرشح للعلاج يمتلك صحة فم جيدة، أي لا يعاني من التسوس أو أمراض اللثة، حيث يجب معالجة هذه المشكلات أولًا قبل البدء بالتقويم.",
              "icon": "fa-solid fa-teeth-open"
            },
            {
              "title": "مشكلات في إطباق الفك",
              "desc": "يُعتبر تقويم الأسنان خيارًا مثاليًا لمن يعانون من مشكلات في الإطباق مثل العضة الزائدة، أو العضة السفلية، أو العضة المفتوحة أو المتصالبة.",
              "icon": "fa-solid fa-teeth"
            },
            {
              "title": "تزاحم الأسنان أو وجود فراغات",
              "desc": "يعالج التقويم الأسنان المتزاحمة أو الملتوية، كما يمكن استخدامه لتصحيح الأسنان المتباعدة وتحقيق اتساق جمالي ووظيفي.",
              "icon": "fa-solid fa-arrows-left-right"
            }
          ]
        },
        "preparationTips": {
          "heading": "الاستعداد",
          "subheading": "",
          "para": "توجد عدة طرق يمكنك من خلالها تحضير أسنانك للعلاج التقويمي، حيث يساهم الاستعداد الجيد قبل بدء العلاج بشكل كبير في نجاحه وسرعة تحقيق النتائج. إليك خطوات التحضير الأساسية:",
          "items": [
            {
              "title": "الاستشارة والتشخيص",
              "desc": "سيقوم طبيب التقويم بمراجعة تاريخك الطبي في الموعد الأول، وإجراء أشعة وفحوصات دقيقة لتصميم خطة علاجية مخصصة لحالتك.",
              "icon": "fa-solid fa-calendar-check"
            },
            {
              "title": "روتين العناية بصحة ونظافة الفم",
              "desc": "قد تصعّب أجهزة التقويم عملية تنظيف الأسنان، لذا يجب الحفاظ على روتين صارم من بداية العلاج.",
              "icon": "fa-solid fa-broom"
            },
            {
              "title": "تعديلات في النظام الغذائي",
              "desc": "تجنب تناول الأطعمة الصلبة أو اللزجة التي قد تتلف التقويم أو تعرقل العلاج.",
              "icon": "fa-solid fa-utensils"
            },
            {
              "title": "فحوصات الأسنان المنتظمة",
              "desc": "احرص على أن تكون أسنانك ولثتك في حالة صحية جيدة قبل بدء التقويم.",
              "icon": "fa-solid fa-calendar-alt"
            }
          ]
        },
        "results": {
          "heading": "النتائج",
          "subheading": "",
          "para": "يُحدث علاج تقويم الأسنان تغييرًا جذريًا في مظهر الوجه ووظائف الفم وصحته. إليك ما يمكن توقعه بعد الانتهاء من العلاج:",
          "items": [
            {
              "title": "أسنان أكثر انتظامًا",
              "desc": "يساعد علاج تقويم الأسنان في الحصول على ابتسامة متناسقة، مما يعزز جمال الوجه.",
              "icon": "fa-solid fa-teeth"
            },
            {
              "title": "تحسين صحة الفم",
              "desc": "يساعد ترتيب الأسنان بشكل صحيح على تسهيل تنظيفها، مما يقلل من خطر الإصابة بتسوس الأسنان، وأمراض اللثة، والمشكلات الأخرى المتعلقة بصحة الفم.",
              "icon": "fa-solid fa-tooth"
            },
            {
              "title": "نتائج تدوم طويلًا",
              "desc": "يساهم استخدام مثبتات الأسنان بعد العلاج في الحفاظ على النتائج لسنوات طويلة.",
              "icon": "fa-solid fa-shield-alt"
            },
            {
              "title": "زيادة الثقة بالنفس",
              "desc": "يشعر العديد من المرضى بارتفاع ملحوظ في مستوى الثقة بالنفس والتفاعل الاجتماعي بعد انتهاء العلاج.",
              "icon": "fa-solid fa-face-smile-beam"
            }
          ]
        },
        "aftercareTips": {
          "heading": "",
          "subheading": "",
          "para": "",
          "items": [
            {
              "title": "",
              "desc": "",
              "icon": "fa-solid fa-water"
            }
          ]
        },
        "faqData": {
          "heading": "الأسئلة الشائعة",
          "subheading": "",
          "items": [
            {
              "question": "كيف أعرف إذا كنت بحاجة إلى واقي فم؟",
              "answer": "تختلف مدة العلاج حسب حالة كل شخص، ولكنها تتراوح عادةً بين 12 إلى 24 شهرًا."
            },
            {
              "question": "هل علاج تقويم الأسنان مؤلم؟",
              "answer": "قد تشعر ببعض الانزعاج خاصة بعد تعديلات التقويم، لكنه يكون محتملاً عادةً ويمكن التغلب عليه باستخدام مسكنات الألم المتاحة دون وصفة طبية."
            },
            {
              "question": "هل هناك بدائل للتقويم المعدني التقليدي؟",
              "answer": "نعم، تتوفر خيارات حديثة مثل التقويم الشفاف والتقويم اللساني (الذي يتم تركيبه خلف الأسنان) للأشخاص الذين يفضلون علاجًا أقل وضوحًا."
            },
            {
              "question": "هل أحتاج إلى مثبت أسنان بعد العلاج؟",
              "answer": "نعم، يعد استخدام المثبت بعد العلاج ضروريًا لمنع عودة الأسنان إلى مواقعها الأصلية."
            },
            {
              "question": "كم مرة يجب أن أزور طبيب التقويم أثناء العلاج؟",
              "answer": "عادةً ما يتم تحديد مواعيد المتابعة كل 4-6 أسابيع لمتابعة التقدم وإجراء التعديلات اللازمة."
            }
          ]
        },
        "treatmentAreas1": [
        ],
        "treatmentAreas2": [

        ]
      },
    shared: {
        images: {
            main: 'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bannersfsdfe-Image-scaled.jpg',
            beforeAfter: [
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-After-estgrdgService-Images-10.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-After-estgrdgService-Images-11.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-After-estgrdgService-Images-12.jpg'
            ],
            introductionImage: '',
        },
        product: {
            id: 22,
            title: 'Orthodontic Treatment',
            price: 1500,
            description: 'Comprehensive orthodontic treatment for all ages.',
        },
    }
};